<script setup lang="ts">
const localePath = useLocalePath()

const props = defineProps<{
  title?: string
  isHomePage?: boolean
  nid?: number
}>()

const { data: newsData, status } = await useLazyCachedAsyncData('hp-news', () =>
  createNewsRepository().getAllPagination(1, 10)
)

const filteredData = computed(() => {
  return newsData.value?.items.filter((newsItem) => newsItem.nid !== props.nid)
})

const firstNews = computed(() => {
  return filteredData.value?.at(0)
})
</script>

<template>
  <section class="pt-16">
    <div class="container">
      <div v-if="status === 'pending'">
        <UiSkeletonLatestNews />
      </div>
      <div v-else-if="filteredData && filteredData?.length">
        <TypographyH2Homepage class="mb-8">
          {{ $t('homepage.news.title') }}
        </TypographyH2Homepage>
        <div
          v-if="firstNews"
          class="-mb-64 flex justify-between gap-10 pb-48 max-lg:flex-col"
        >
          <div class="w-full max-w-2xl">
            <div class="aspect-[3/2] w-full">
              <NuxtLink
                :to="
                  localePath({
                    name: 'news-slug',
                    params: { slug: firstNews.urlSlug },
                  })
                "
              >
                <NuxtImg
                  v-if="firstNews.thumbnailImage?.url"
                  class="h-full w-full object-cover"
                  :src="withHost(firstNews.thumbnailImage?.url)"
                  sizes="100vw lg:672px"
                />
              </NuxtLink>
            </div>
          </div>
          <div class="flex flex-col justify-between gap-8">
            <div class="flex flex-col gap-8">
              <time v-if="firstNews.releaseDate" class="block text-sm">
                {{
                  new Intl.DateTimeFormat('cs-CZ', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).format(new Date(firstNews.releaseDate))
                }}
              </time>
              <NuxtLink
                :to="
                  localePath({
                    name: 'news-slug',
                    params: { slug: firstNews.urlSlug },
                  })
                "
                class="no-underline"
              >
                <h2 class="font-pragmatica-ext text-4xl font-bold">
                  {{ firstNews.title }}
                </h2>
              </NuxtLink>
              <p class="lg:max-w-md">
                {{ truncate(stripHtml(firstNews.content ?? '') ?? '', 250) }}
              </p>
            </div>
            <NuxtLink
              :to="
                localePath({
                  name: 'news-slug',
                  params: { slug: firstNews.urlSlug },
                })
              "
            >
              <UiButtonBlack>
                {{ $t('homepage.news.cta') }}
              </UiButtonBlack>
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>
    <div class="overflow-hidden bg-neutral-100 pb-8 pt-48">
      <div class="container">
        <div class="block">
          <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <template v-if="status === 'pending'">
              <div v-for="i in 3" :key="i" class="basis-auto">
                <UiSkeletonReferencesCard dark />
              </div>
            </template>
            <template v-else>
              <div
                v-for="newsItem in filteredData?.slice(1, 4)"
                :key="newsItem.nid"
              >
                <div class="mb-5 aspect-[3/2] w-full">
                  <NuxtLink
                    :to="
                      localePath({
                        name: 'news-slug',
                        params: { slug: newsItem.urlSlug },
                      })
                    "
                  >
                    <NuxtImg
                      loading="lazy"
                      class="h-full w-full object-cover"
                      :src="withHost(newsItem.thumbnailImage?.url || '')"
                      sizes="300px lg:672px"
                    />
                  </NuxtLink>
                </div>
                <div>
                  <NuxtLink
                    :to="
                      localePath({
                        name: 'news-slug',
                        params: { slug: newsItem.urlSlug },
                      })
                    "
                    class="block w-fit text-sm no-underline first-letter:uppercase"
                  >
                    <h2
                      class="font-pragmatica-ext mb-2.5 text-ellipsis text-2xl font-bold"
                    >
                      {{ newsItem.title }}
                    </h2>
                  </NuxtLink>
                  <div class="flex gap-4 text-sm">
                    <NuxtLink
                      :to="
                        localePath({
                          name: 'news-slug',
                          params: { slug: newsItem.urlSlug },
                        })
                      "
                      class="block w-fit text-sm no-underline first-letter:uppercase"
                    >
                      <UiAnimatedUnderlineText>
                        {{ $t('homepage.news.cta') }}
                      </UiAnimatedUnderlineText>
                    </NuxtLink>
                    <time v-if="newsItem.releaseDate">
                      {{
                        new Intl.DateTimeFormat('cs-CZ', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        }).format(new Date(newsItem.releaseDate))
                      }}
                    </time>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
        <div class="mx-auto mt-8 block w-fit">
          <NuxtLink
            class="text-sm no-underline"
            :to="localePath({ name: 'news' })"
          >
            <UiAnimatedUnderlineText>
              {{ $t('homepage.news.seeMore') }}
            </UiAnimatedUnderlineText>
          </NuxtLink>
        </div>
      </div>
    </div>
  </section>
</template>
